import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { AppHelper } from 'helpers/appHelper';
import { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { ReportRefItemType } from 'types/api/quizReportRefResponseType';

import '../../../assets/styles/ws-report.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

/**
 *　レポートチャート描画コンポーネント
 */
export default function WsReportChart(props: { reports: ReportRefItemType[] }): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type ChartType =
    | 'participatedCount'
    | 'mailAddressCount'
    | 'mailAddressAcquisitionRate'
    | 'answeredRate';

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [initialized, setInitialized] = useState(false);

  const [chartTypeState, setChartTypeState] = useState<ChartType>(null);
  const [chartDataState, setChartDataState] = useState(null);
  const [chartOptionsState, setChaartOptionsState] = useState(null);

  const chartRef = useRef(null);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * レポートデータが変更された場合の処理
   */
  useEffect(() => {
    // 初期読み込み時は参加数をデフォルト表示
    if (chartTypeState === null) {
      const chartType = 'participatedCount';
      setChartTypeState(chartType);

      _drawChart(chartType);
      setInitialized(true);
    }
    // 初期読み込み後は、チャート種別を維持して再描画
    else {
      _drawChart(chartTypeState);
    }
  }, [props.reports]);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * チャート種別変更ボタン押下時の処理
   */
  const onChangeChartType = (chartType: ChartType) => {
    setChartTypeState(chartType);
    _drawChart(chartType);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画面の初期化処理を行います。
   */
  const _drawChart = (chartType: ChartType) => {
    const labels = props.reports.map(item => {
      return item.label;
    });
    let data = [];

    if (chartType === 'participatedCount') {
      data = props.reports.map(item => {
        return item.participatedCount;
      });
    } else if (chartType === 'mailAddressCount') {
      data = props.reports.map(item => {
        return item.mailAddressCount;
      });
    } else if (chartType === 'mailAddressAcquisitionRate') {
      data = props.reports.map(item => {
        return AppHelper.formatRateToPercent(item.mailAddressAcquisitionRate);
      });
    } else if (chartType === 'answeredRate') {
      data = props.reports.map(item => {
        return AppHelper.formatRateToPercent(item.answeredRate);
      });
    }

    const baseColor = _getBaseColor(chartType);
    const backgroundColor = _getBackgroundColor(chartType);

    const maxVal = Math.max(...data);

    setChartDataState({
      labels: labels,
      datasets: [
        {
          data: data,
          fill: true,
          backgroundColor: backgroundColor,
          borderColor: baseColor,
          pointBackgroundColor: baseColor,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: baseColor,
          tension: 0.1,
        },
      ],
    });

    setChaartOptionsState({
      // responsive: true,
      plugins: {
        legend: {
          display: false,
        },
      },
      elements: {
        point: {
          radius: 5,
        },
      },
      scales:
        chartType === 'mailAddressAcquisitionRate' || chartType === 'answeredRate'
          ? {
              y: {
                max: 100,
                min: 0,
              },
            }
          : {
              y: {
                max: maxVal > 100 ? maxVal : 100,
                min: 0,
              },
            },
    });

    if (chartRef.current) {
      chartRef.current.update();
    }
  };

  /**
   * レポート種別やグラフの本職を取得します。
   */
  const _getBaseColor = (chartType: ChartType) => {
    if (chartType === 'participatedCount') {
      return '#7d7bd1';
    } else if (chartType === 'mailAddressCount') {
      return '#fec53d';
    } else if (chartType === 'mailAddressAcquisitionRate') {
      return '#4ad9d9';
    } else if (chartType === 'answeredRate') {
      return '#ff738d';
    }
  };

  /**
   * グラフの背景色を取得します。
   */
  const _getBackgroundColor = (chartType: ChartType) => {
    if (chartType === 'participatedCount') {
      return '#eef3fe';
    } else if (chartType === 'mailAddressCount') {
      return '#fff3d9';
    } else if (chartType === 'mailAddressAcquisitionRate') {
      return '#dcf7f7';
    } else if (chartType === 'answeredRate') {
      return '#ffe0e6';
    }
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {initialized && (
        <section className="section-chart">
          <div className="container">
            <div className="card card-chart">
              <div className="card-header">
                <ul className="list-unstyled list-inline list-badge mb-0">
                  <li className="list-inline-item">
                    <button
                      onClick={() => onChangeChartType('participatedCount')}
                      className={`btn btn-simple ${
                        chartTypeState === 'participatedCount' ? 'participatedCount' : 'gray'
                      }`}
                      type="button"
                    >
                      参加数
                    </button>
                  </li>
                  <li className="list-inline-item">
                    <button
                      onClick={() => onChangeChartType('mailAddressCount')}
                      className={`btn btn-simple ${
                        chartTypeState === 'mailAddressCount' ? 'mailAddressCount' : 'gray'
                      }`}
                      type="button"
                    >
                      獲得リスト数
                    </button>
                  </li>
                  <li className="list-inline-item">
                    <button
                      onClick={() => onChangeChartType('mailAddressAcquisitionRate')}
                      className={`btn btn-simple ${
                        chartTypeState === 'mailAddressAcquisitionRate'
                          ? 'mailAddressAcquisitionRate'
                          : 'gray'
                      }`}
                      type="button"
                    >
                      リスト獲得率
                    </button>
                  </li>
                  <li className="list-inline-item">
                    <button
                      onClick={() => onChangeChartType('answeredRate')}
                      className={`btn btn-simple ${
                        chartTypeState === 'answeredRate' ? 'answeredRate' : 'gray'
                      }`}
                      type="button"
                    >
                      完了率
                    </button>
                  </li>
                </ul>
              </div>
              <div className="card-body pt-3">
                <Line
                  ref={chartRef}
                  data={chartDataState}
                  options={chartOptionsState}
                  redraw={true}
                  style={{ maxHeight: '500px' }}
                />

                {/* <canvas id="dataChart" style={{ width: "100%" }}></canvas> */}
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
